// `avatar sync --all` — sync MỌI workspace đã đăng ký trên máy (#211 phase-02).
//
// ⚠️ Nhánh này rẽ trong `action()` của `sync`, TRƯỚC `syncAction` ⇒ KHÔNG thừa hưởng thứ gì
// của `syncAction`. Ba thứ phải khai lại, không phải hai:
//   1. `setMachineReadableMode(true)` khi thấy `--json` — cờ module-level, chuyển mọi `log.*`
//      sang stderr. Quên ⇒ log người-đọc rơi vào stdout TRƯỚC dòng JSON ⇒ badge extension
//      parse chết (đúng lớp lỗi #280).
//   2. Từ chối `--local` + `--pack-version` (luật `sync.ts:190-193`).
//   3. Từ chối `--json` không kèm `--dry-run` (luật `sync.ts:163-166`), báo bằng CHÍNH JSON.
//   4. `--reinstall` (force update 13/10): planner KHÔNG xếp `same-version` vào skipped (trước
//      đây vì bộ lọc này mà cờ bị từ chối — forward xuống con là vô tác dụng, im lặng exit 0),
//      và cờ được forward xuống từng con. Luật an toàn (stale/open-session/mission) giữ nguyên.
// Đổi lại, `--all` KHÔNG đi qua `assertInAvatarWorkspaceOrExit` — cố ý: lệnh phải gõ được từ
// thư mục bất kỳ (kể cả `~`), đó là toàn bộ lý do ticket tồn tại.
import { homedir } from "node:os";
import { resolve } from "node:path";

import { collectAllWorkspaceFacts } from "../lib/collect-workspace-facts-for-sync-all.js";
import { objectToVersion } from "../lib/download-team-pack-and-write-manifest.js";
import { ensureValidTokenOrLogin } from "../lib/ensure-valid-token-or-login.js";
import {
  writeMachineReadableError,
  writeMachineReadableJson,
} from "../lib/machine-readable-json-output-contract.js";
import { isNonInteractiveFatalError } from "../lib/non-interactive-abort-error.js";
import { isNonInteractive } from "../lib/non-interactive-context.js";
import { LOGIN_REQUIRED_EXIT_CODE } from "../lib/non-interactive-login-required-error.js";
import {
  DEFAULT_STALE_DAYS,
  planSyncAllAcrossRegisteredWorkspaces,
  type WorkspaceDecision,
} from "../lib/plan-sync-all-across-registered-workspaces.js";
import { readLocalPackVersion } from "../lib/read-local-pack-version.js";
import { removeNonDevWorkspacesFromRegistry } from "../lib/remove-non-dev-workspaces-from-registry.js";
import { spawnSyncInWorkspace } from "../lib/spawn-sync-in-workspace.js";
import { fetchSignedPackUrl } from "../lib/supabase-pack-downloader.js";
import { log, setMachineReadableMode } from "../lib/terminal-logger.js";
import { markWorkspaceSynced, pruneMissingWorkspaces } from "../lib/workspace-registry-store.js";
import { renderSyncAllSummaryTable, type SummaryRow } from "./render-sync-all-summary-table.js";

export interface SyncAllOptions {
  force?: boolean;
  packVersion?: string;
  dryRun?: boolean;
  local?: string;
  json?: boolean;
  staleDays?: string;
  includeDowngrades?: boolean;
  forceOpenSessions?: string[];
  reinstall?: boolean;
}

const COMMAND = "sync-all";

/** Lỗi dùng-sai-cờ: ở chế độ `--json` phải trả JSON, đừng bắt extension parse text. */
function refuse(message: string, json: boolean | undefined): never {
  if (json) writeMachineReadableError(COMMAND, message);
  else log.error(message);
  process.exit(1);
}

function parseStaleDays(raw: string | undefined, json: boolean | undefined): number {
  if (raw === undefined) return DEFAULT_STALE_DAYS;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0) refuse(`--stale-days phải là số ngày ≥ 0 (nhận: ${raw})`, json);
  return n;
}

/**
 * Version ĐÍCH, tra ĐÚNG MỘT LẦN cho cả registry.
 *
 * `--pack-version`/`--local` bỏ qua bước mạng — đây cũng là 2 seam để test 5 luật lọc mà
 * không chạm Supabase. Đường trần hỏi server 1 lần (`fetchSignedPackUrl`, đo 3.87s, KHÔNG
 * tải tarball) và resolve token 1 lần ở CHA: token chết giữa vòng lặp sẽ bật login tương tác
 * ở từng tiến trình con, N lần.
 */
async function resolveTargetVersion(opts: SyncAllOptions): Promise<string> {
  if (opts.packVersion) return opts.packVersion;
  if (opts.local) return await readLocalPackVersion(resolve(opts.local));

  let idToken: string;
  try {
    idToken = await ensureValidTokenOrLogin({ command: COMMAND });
  } catch (err) {
    // Non-interactive + token chết ⇒ nổi lên `index.ts`: JSON `login_required` + exit 3.
    if (isNonInteractiveFatalError(err)) throw err;
    refuse(err instanceof Error ? err.message : String(err), opts.json);
  }
  const info = await fetchSignedPackUrl(idToken);
  return objectToVersion(info.object);
}

function toJsonPayload(
  decisions: readonly WorkspaceDecision[],
  prunedPaths: readonly string[],
  targetVersion: string,
): Record<string, unknown> {
  // KHÔNG tự thêm `schema_version` — `writeMachineReadableJson` gắn sẵn và gán ĐÈ sau spread.
  return {
    command: COMMAND,
    target_version: targetVersion,
    will_sync: decisions
      .filter((d) => d.kind === "will-sync")
      .map((d) => ({ path: d.path, current_version: d.currentVersion })),
    skipped: decisions
      .filter((d) => d.kind === "skipped")
      .map((d) => ({ path: d.path, current_version: d.currentVersion, reason: d.reason })),
    pruned: [
      ...prunedPaths.map((path) => ({ path, current_version: null })),
      ...decisions
        .filter((d) => d.kind === "pruned")
        .map((d) => ({ path: d.path, current_version: null })),
    ],
    failed: [],
  };
}

function skippedRows(decisions: readonly WorkspaceDecision[]): SummaryRow[] {
  return decisions
    .filter((d) => d.kind === "skipped")
    .map((d) => ({
      path: d.path,
      outcome: "skipped" as const,
      currentVersion: d.currentVersion,
      reason: d.reason,
      detail: d.detail,
    }));
}

export async function runSyncAllAcrossRegisteredWorkspaces(opts: SyncAllOptions): Promise<void> {
  // Luật 4 của hợp đồng máy-đọc: bật TRƯỚC mọi `log.*` của nhánh này.
  if (opts.json) setMachineReadableMode(true);
  if (opts.json && !opts.dryRun) refuse("--json chỉ dùng cùng --dry-run", true);
  if (opts.local && opts.packVersion) {
    refuse(
      "--local không dùng chung với --pack-version (version chỉ áp dụng cho Supabase).",
      opts.json,
    );
  }

  const staleDays = parseStaleDays(opts.staleDays, opts.json);
  const forceOpenSessionPaths = (opts.forceOpenSessions ?? []).map((p) => resolve(p));

  // Prune GHI THẬT xuống `workspaces.json` — KỂ CẢ `--dry-run`. Ngoại lệ có chủ đích:
  // `--dry-run` nhịn phần CÀI PACK, không nhịn phần DỌN entry chết. Chỉ đi MỘT CHIỀU (bớt),
  // không bao giờ tạo entry mới ⇒ badge extension gọi lặp bao nhiêu lần cũng an toàn.
  const { kept, pruned } = await pruneMissingWorkspaces();

  const targetVersion = await resolveTargetVersion(opts);
  const now = Date.now();
  const facts = await collectAllWorkspaceFacts(kept, homedir(), now);
  const decisions = planSyncAllAcrossRegisteredWorkspaces(facts, {
    targetVersion,
    now,
    staleDays,
    includeDowngrades: opts.includeDowngrades,
    forceOpenSessionPaths,
    reinstall: opts.reinstall,
  });
  // #254: workspace non-dev (vd Avatar DataHub) ⇒ gỡ khỏi registry, kể cả --dry-run (một chiều).
  await removeNonDevWorkspacesFromRegistry(decisions).catch(() => []);

  if (opts.dryRun) {
    if (opts.json) {
      writeMachineReadableJson(toJsonPayload(decisions, pruned, targetVersion));
      return;
    }
    const rows: SummaryRow[] = [
      ...pruned.map((path) => ({ path, outcome: "pruned" as const, currentVersion: null })),
      ...decisions
        .filter((d) => d.kind === "will-sync")
        .map((d) => ({
          path: d.path,
          outcome: "will-sync" as const,
          currentVersion: d.currentVersion,
        })),
      ...skippedRows(decisions),
    ];
    renderSyncAllSummaryTable(rows, targetVersion);
    log.info("Dry-run: không workspace nào bị đụng. Bỏ --dry-run để thực thi.");
    return;
  }

  await fanOutAndReport(decisions, pruned, targetVersion, opts);
}

/**
 * Chạy TUẦN TỰ từng workspace `will-sync`. 1 con vỡ không giết vòng lặp (AC8) — cha chỉ đọc
 * exit code, không có exception nào bay lên.
 *
 * Ngắt giữa chừng (SIGINT/SIGTERM): NGỪNG spawn workspace kế tiếp, CHỜ con hiện tại (tín hiệu
 * đã tự tới nó vì cùng process group do `stdio: "inherit"`), rồi in bảng một phần. Workspace
 * đang dở phải được nêu ĐÍCH DANH: `atomicSwapDir` là 2 lần rename, bị giết đúng giữa 2 lần
 * đó thì `.claude/pack` VẮNG MẶT và symlink farm trỏ hụt.
 */
async function fanOutAndReport(
  decisions: readonly WorkspaceDecision[],
  prunedPaths: readonly string[],
  targetVersion: string,
  opts: SyncAllOptions,
): Promise<void> {
  const targets = decisions.filter((d) => d.kind === "will-sync");
  const rows: SummaryRow[] = [
    ...prunedPaths.map((path) => ({ path, outcome: "pruned" as const, currentVersion: null })),
    ...skippedRows(decisions),
  ];

  let interrupted = false;
  // Con exit 3 (`login_required`): token chết GIỮA vòng, hoặc cha bỏ bước token vì
  // `--pack-version`/`--local`. Mọi con sau cũng sẽ chết y hệt ⇒ dừng, đừng đốt N lần.
  let loginRequired = false;
  let running: string | null = null;
  // Workspace ĐANG chạy đúng lúc tín hiệu tới — thứ duy nhất có thể còn `.claude/pack` vắng.
  let interruptedDuring: string | null = null;
  const onSignal = (): void => {
    interrupted = true;
    interruptedDuring = running;
  };
  process.on("SIGINT", onSignal);
  process.on("SIGTERM", onSignal);

  try {
    for (const target of targets) {
      if (interrupted || loginRequired) {
        rows.push({
          path: target.path,
          outcome: "not-reached",
          currentVersion: target.currentVersion,
        });
        continue;
      }
      log.info(`\n── sync ${target.path} (đang cài: ${target.currentVersion ?? "unknown"})`);
      running = target.path;
      const result = await spawnSyncInWorkspace(target.path, {
        force: opts.force,
        packVersion: opts.packVersion,
        local: opts.local,
        reinstall: opts.reinstall,
        // Cờ `--non-interactive` của cha là trạng thái module, KHÔNG tự sang con (env thì có).
        nonInteractive: isNonInteractive(),
      });
      running = null;

      if (result.code === 0) {
        // Tín hiệu AC9 đo "sync KHÔNG chạy" — chỉ ghi khi con thật sự exit 0.
        await markWorkspaceSynced(target.path, new Date());
        rows.push({ path: target.path, outcome: "ok", currentVersion: target.currentVersion });
      } else {
        loginRequired = result.code === LOGIN_REQUIRED_EXIT_CODE;
        rows.push({
          path: target.path,
          outcome: "failed",
          currentVersion: target.currentVersion,
          detail: result.signal
            ? `bị tín hiệu ${result.signal}`
            : loginRequired
              ? "cần đăng nhập (login_required) — chạy `avatar login` rồi thử lại"
              : `exit code ${result.code ?? "?"}`,
        });
      }
    }
  } finally {
    process.off("SIGINT", onSignal);
    process.off("SIGTERM", onSignal);
  }

  renderSyncAllSummaryTable(rows, targetVersion);

  if (interrupted) {
    const unfinished = interruptedDuring;
    log.warn(
      `Đã ngắt giữa chừng.${unfinished ? ` Workspace đang dở: ${unfinished}` : ""}\n` +
        "  Pack ở đó có thể đang VẮNG — chạy lại `avatar sync` tại workspace này.\n" +
        "  Còn sót `.claude/pack.old-*` / `.claude/pack.new-*` thì xoá tay sau khi sync lại.",
    );
    process.exit(130);
  }
  // Giữ mã 3 của con: phía gọi (updater) phân biệt "cần đăng nhập" với "sync hỏng".
  if (loginRequired) process.exit(LOGIN_REQUIRED_EXIT_CODE);
  // `pruned` KHÔNG phải lỗi: phần lớn marker trên máy là thư mục thử đã xoá (đo 2026-08-20).
  if (rows.some((r) => r.outcome === "failed")) process.exit(1);
}
